/**
 * assets/bp-shipping-week.js
 *
 * Manages the shipping week selector on plant PDPs.
 *
 * The <select> and its <option> elements are server-rendered by Liquid from
 * the union of all variants' inventory-by-week entries. This script's job is to:
 *   1. Filter options to those within the customer's cutoff date window for
 *      the variant's shipping set (from grow.getCutoffDates()).
 *   2. Disable options with no inventory for the selected variant.
 *   3. Auto-select the earliest in-stock visible option ("Recommended").
 *   4. Render a "Plant in {location} from {earliest} to {latest}" message.
 *   5. Render an inline zip code form when no zip/zone is available yet.
 *   6. Mirror the Add to Cart button's zip/state gating onto the Notify Me
 *      button for out-of-stock Plant Products: disabled while a
 *      current-season shipping set exists but the zip is unknown, always
 *      available when there's no current-season shipping set, and hidden
 *      entirely (along with the OOS note) when shipment is state-restricted.
 *
 * Depends on bp-grow-module.js being loaded first, which exposes:
 *   window.getZipCode(), getGrowingZone(), getState(),
 *   fetchGrowData(zip), setLocalStorage(prefs), showCustomerZipCodeForm(),
 *   getCutoffDates(), getCurrentSeason()
 */

import * as grow from "@theme/bp-grow-module";

/**
 * A single shipping week entry as stored in the schedule JSON.
 *
 * @typedef {Object} ShippingWeek
 * @property {string} shipDate       - ISO date string (YYYY-MM-DD). The Monday the order ships.
 * @property {string} displayName    - Human-readable range shown to the customer, e.g. "Apr 06 - Apr 17".
 * @property {string} lastOrderDate  - ISO date string (YYYY-MM-DD). Last day a customer can order for this week.
 */

/**
 * A single inventory entry from the inventory-by-week variant metafield.
 *
 * @typedef {Object} InventoryEntry
 * @property {string} shipDate  - ISO date string (YYYY-MM-DD). Matches ShippingWeek.shipDate.
 * @property {number} qty       - Available inventory quantity for this ship date.
 */

/**
 * The inventory-by-week metafield value for a single variant.
 *
 * @typedef {Object} VariantInventory
 * @property {InventoryEntry[]} inventory
 */

/**
 * Per-variant inventory map embedded on the <select>, keyed by variant ID string.
 *
 * @typedef {Record<string, VariantInventory>} InventoryMap
 */

/**
 * Per-variant shipping set GID map embedded on the <select>, keyed by variant ID string.
 *
 * @typedef {Record<string, string>} ShippingSetsMap
 */

const ADD_TO_CART_SELECTOR = '[name="add"]';
const HIDDEN_DISPLAY_ID = "shipping-week-display";

/** @type {InventoryMap} */
let inventory = {};

/** @type {ShippingSetsMap} */
let shippingSets = {};

/** @type {HTMLOptionElement[]} */
let originalOptions = [];

/** @type {Record<string, string>} */
let shippingMessages = {};

const INVENTORY_CAP = 50;
const QUANTITY_INPUT_SELECTOR = '[name="quantity"]';

/** @type {Record<string, string>} */
let stateRestrictions = {};

/** @type {Set<string>} */
let outOfSeasonPlants = new Set();

const FALLBACK_SHIPPING_MESSAGE = "1 - 3 Business Days";

document.addEventListener("DOMContentLoaded", async () => {
  const select = /** @type {HTMLSelectElement} */ (
    document.querySelector(".bp-shipping-week-dropdown")
  );

  const hiddenField = /** @type {HTMLInputElement | null} */ (
    document.getElementById(HIDDEN_DISPLAY_ID)
  );

  const messagesSource = select ?? hiddenField;
  try {
    shippingMessages =
      JSON.parse(messagesSource?.dataset.shippingMessages ?? "null") ?? {};
  } catch (e) {
    // TODO: Report JSON parse error to LogRocket
  }

  const cannotShipRoot = document.getElementById("bp-cannot-ship-root");
  try {
    stateRestrictions =
      JSON.parse(cannotShipRoot?.dataset.stateRestrictions ?? "null") ?? {};
  } catch (e) {
    // TODO: Report JSON parse error to LogRocket
  }

  if (!select) {
    const initialVariantId = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[name="id"]')
    )?.value;
    if (initialVariantId && hiddenField) {
      hiddenField.value =
        shippingMessages[String(initialVariantId)] ?? FALLBACK_SHIPPING_MESSAGE;
    }

    if (initialVariantId) {
      applyShippingRestriction(String(initialVariantId));
    }

    window.addEventListener("onLocationChange", () => {
      const variantId = document.querySelector('[name="id"]')?.value;
      if (variantId) {
        applyShippingRestriction(variantId);
        if (!isStateRestricted(variantId)) {
          setAddToCartDisabled(false);
        }
      }
    });

    document.addEventListener(
      "variant:update",
      (/** @type {CustomEvent} */ e) => {
        const variantId = /** @type {any} */ (e).detail?.resource?.id;
        if (variantId && hiddenField) {
          hiddenField.value =
            shippingMessages[String(variantId)] ?? FALLBACK_SHIPPING_MESSAGE;
        }
        if (variantId) {
          applyShippingRestriction(String(variantId));
          if (!isStateRestricted(String(variantId))) {
            setAddToCartDisabled(false);
          }
        }
      },
    );
    return;
  }

  try {
    inventory = JSON.parse(select.dataset.inventory ?? "null");
    shippingSets = JSON.parse(select.dataset.shippingSets ?? "null") ?? {};
    outOfSeasonPlants = new Set(
      (JSON.parse(select.dataset.outOfSeasonPlants ?? "null") ?? []).map(String),
    );
  } catch (e) {
    // TODO: Report parse failure to logging system.
    return;
  }

  const initialVariantId = /** @type {HTMLInputElement | null} */ (
    document.querySelector('[name="id"]')
  )?.value;

  originalOptions = initialVariantId
    ? buildOptionsForVariant(String(initialVariantId))
    : Array.from(select.options).filter((opt) => opt.value);

  removeSkeleton();

  if (initialVariantId) {
    applyShippingRestriction(String(initialVariantId));
  }

  if (initialVariantId && outOfSeasonPlants.has(String(initialVariantId))) {
    setSelectorVisible(select, false);
    renderPlantOutOfStock();
    setAddToCartDisabled(true);
    setNotifyMeDisabled(false);
  } else if (!initialVariantId || !shippingSets[initialVariantId]) {
    setSelectorVisible(select, false);
    setNotifyMeDisabled(false);
  } else if (!isStateRestricted(String(initialVariantId))) {
    initDropdown(select, initialVariantId);
  }

  window.addEventListener("onLocationChange", () => {
    const variantId = document.querySelector('[name="id"]')?.value;
    if (variantId) {
      applyShippingRestriction(variantId);
      if (!isStateRestricted(variantId)) {
        if (shippingSets[variantId]) {
          initDropdown(select, variantId);
        } else if (!outOfSeasonPlants.has(variantId)) {
          setAddToCartDisabled(false);
          setNotifyMeDisabled(false);
        }
      }
    }
  });

  document.addEventListener(
    "variant:update",
    (/** @type {CustomEvent} */ e) => {
      const variantId = String(
        /** @type {any} */ (e).detail?.resource?.id ?? "",
      );
      if (!variantId) {
        return;
      }

      applyShippingRestriction(variantId);

      if (outOfSeasonPlants.has(variantId)) {
        setSelectorVisible(select, false);
        const label = document.getElementById("shipping-date-label");
        if (label) {
          label.style.display = "none";
        }
        renderPlantOutOfStock();
        setAddToCartDisabled(true);
        setNotifyMeDisabled(false);
        return;
      }

      if (!shippingSets[variantId]) {
        setSelectorVisible(select, false);
        clearShippingProperties(select, variantId);
        const label = document.getElementById("shipping-date-label");
        if (label) {
          label.style.display = "none";
        }
        setNotifyMeDisabled(false);

        if (e.detail?.resource?.available && !isStateRestricted(variantId)) {
          setAddToCartDisabled(false);
        }

        const root = document.getElementById("bp-shipping-week-root");
        if (root) {
          root.innerHTML = "";
        }
        return;
      }

      if (isStateRestricted(variantId)) {
        return;
      }

      originalOptions = buildOptionsForVariant(variantId);
      initDropdown(select, variantId);
    },
  );

  select.addEventListener("change", () => {
    const variantId =
      /** @type {HTMLInputElement | null} */ (
        document.querySelector('[name="id"]')
      )?.value ?? "";
    syncPlantFields(select, variantId);
    // SHOP-782: re-cap the PDP quantity whenever the customer picks another week.
    const chosenShipDate = select.options[select.selectedIndex]?.dataset.shipDate;
    const variantInv = inventory?.[variantId]?.inventory ?? [];
    const entry = variantInv.find((e) => e.shipDate === chosenShipDate);
    applyQuantityCap(entry?.qty);
  });
});

/**
 * Returns true if the customer's current state is in the restricted list for
 * the given variant.
 *
 * @param {string} variantId
 * @returns {boolean}
 */
function isStateRestricted(variantId) {
  const state = grow.getState();
  if (!state) {
    return false;
  }
  const restrictions = stateRestrictions[variantId] ?? "";
  if (!restrictions) {
    return false;
  }
  return restrictions
    .split(",")
    .map((s) => s.trim())
    .includes(state);
}

/**
 * Checks whether the current state is restricted for the given variant and
 * renders or clears the cannot-ship message accordingly. Also disables or
 * re-enables the Add to Cart button and hides/shows #bp-shipping-week-wrapper.
 *
 * @param {string} variantId
 * @returns {void}
 */
function applyShippingRestriction(variantId) {
  const root = document.getElementById("bp-cannot-ship-root");
  const wrapper = document.getElementById("bp-shipping-week-wrapper");
  const restricted = isStateRestricted(variantId);

  setNotifyMeVisible(!restricted);

  if (restricted) {
    setAddToCartDisabled(true);
    if (wrapper) {
      wrapper.style.display = "none";
    }
    if (root) {
      renderCannotShip(root);
    }
  } else {
    if (wrapper) {
      wrapper.style.display = "";
    }
    if (root) {
      root.innerHTML = "";
    }
  }
}

/**
 * Renders the "cannot ship to {state}, {zip} due to restrictions" message
 * into the given root element.
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function renderCannotShip(root) {
  root.innerHTML = "";

  const msg = document.createElement("div");
  msg.className = "bp-cannot-ship";

  const p = document.createElement("p");
  p.appendChild(
    document.createTextNode(`This product cannot be shipped to ${grow.getState()}, `),
  );

  const zipSpan = document.createElement("span");
  zipSpan.className = "bp-zip-edit";
  zipSpan.textContent = grow.getZipCode();
  zipSpan.setAttribute("role", "button");
  zipSpan.setAttribute("tabindex", "0");
  zipSpan.setAttribute("aria-label", `Edit zip code: ${grow.getZipCode()}`);
  zipSpan.setAttribute("aria-haspopup", "dialog");
  zipSpan.addEventListener("click", () => grow.showCustomerZipCodeForm());
  zipSpan.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      grow.showCustomerZipCodeForm();
    }
  });
  p.appendChild(zipSpan);

  p.appendChild(document.createTextNode(" due to restrictions"));

  msg.appendChild(p);
  root.appendChild(msg);
}

/**
 * Caps the PDP quantity input at the selected week's stock so customers cannot
 * add more plants than the chosen weekly bucket holds (SHOP-782). Weeks whose
 * inventory is at/above the public API cap (=50, i.e. "plenty") lift the cap.
 *
 * @param {number | undefined} qtyForSelectedWeek
 * @returns {void}
 */
function applyQuantityCap(qtyForSelectedWeek) {
  const input = /** @type {HTMLInputElement | null} */ (
    document.querySelector(QUANTITY_INPUT_SELECTOR)
  );
  if (!input) {
    return;
  }
  if (qtyForSelectedWeek === undefined || qtyForSelectedWeek >= INVENTORY_CAP) {
    input.removeAttribute("max");
    return;
  }
  const max = Math.max(1, qtyForSelectedWeek);
  input.max = String(max);
  const current = parseInt(input.value, 10);
  if (!Number.isNaN(current) && current > max) {
    input.value = String(max);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

/**
 * Top-level orchestrator. Checks whether the customer has a zip code in
 * localStorage, then either renders the inline zip prompt or runs the
 * two-pass filter (cutoff date window → inventory state) on the dropdown.
 *
 * @param {HTMLSelectElement} select    - The augmented shipping week <select> element.
 * @param {string}            variantId - The active Shopify variant ID.
 * @returns {void}
 */
function initDropdown(select, variantId) {
  const zipCode = grow.getZipCode();

  if (!zipCode) {
    setSelectorVisible(select, false);
    renderZipPrompt(select);
    setAddToCartDisabled(true);
    setNotifyMeDisabled(true);
    return;
  }

  // Zip is known - the Notify Me button is never blocked for this reason again,
  // even if this variant later turns out to have no in-stock ship weeks.
  setNotifyMeDisabled(false);

  const root = document.getElementById("bp-shipping-week-root");
  if (root) {
    root.innerHTML = "";
  }

  const label = document.getElementById("shipping-date-label");
  if (label) {
    label.style.display = "none";
  }

  const hasWeeks = applyZoneFilter(select, variantId);

  if (!hasWeeks) {
    setSelectorVisible(select, false);
    renderNoAvailability();
    setAddToCartDisabled(true);
    return;
  }

  applyInventory(select, variantId);
}

/**
 * Filters the dropdown options to those within the cutoff date window for
 * the variant's shipping set, and removes options whose order cutoff has passed.
 *
 * @param {HTMLSelectElement} select    - The shipping week <select> element.
 * @param {string}            variantId - The active Shopify variant ID.
 * @returns {boolean} Whether any valid options remain after filtering.
 */
function applyZoneFilter(select, variantId) {
  const setGid = shippingSets[variantId];
  const cutoffDates = grow.getCutoffDates();
  const range = setGid && cutoffDates ? cutoffDates[setGid] : null;
  const today = window.bpGetDate();

  for (const opt of Array.from(select.options)) {
    if (opt.value) {
      opt.remove();
    }
  }

  for (const opt of originalOptions) {
    const shipDate = opt.dataset.shipDate ?? "";
    const lastOrderDate = opt.dataset.lastOrderDate ?? "";
    const pastCutoff = lastOrderDate < today;
    const outOfRange = range
      ? shipDate < range.minimum || shipDate > range.maximum
      : true;
    if (!pastCutoff && !outOfRange) {
      select.appendChild(opt);
    }
  }

  return Array.from(select.options).some((opt) => opt.value);
}

/**
 * Evaluates stock for every visible <option> against the given variant's
 * inventory data, then:
 *   - Disables sold-out options and appends " (Sold out)" to their label.
 *   - Selects the earliest in-stock option and appends " (Recommended)".
 *   - Resets the select to the placeholder if every visible option is sold out.
 *   - Enables or disables the Add to Cart button accordingly.
 *
 * @param {HTMLSelectElement} select     - The augmented shipping week <select> element.
 * @param {string}            variantId  - The Shopify variant ID to look up inventory for.
 * @returns {void}
 */
function applyInventory(select, variantId) {
  const inventoryEntries = inventory?.[variantId]?.inventory ?? [];

  /** @type {Record<string, number>} */
  const qtyByDate = {};
  for (const entry of inventoryEntries) {
    qtyByDate[entry.shipDate] = entry.qty;
  }

  let recommendedSet = false;
  let allSoldOut = true;

  for (const option of select.options) {
    if (!option.value) {
      continue;
    }

    const qty = qtyByDate[option.dataset.shipDate ?? ""] ?? 0;
    const inStock = qty > 0;

    option.textContent = `Ships ${option.dataset.display}`;
    option.disabled = false;
    option.selected = false;

    if (!inStock) {
      option.textContent += " (Sold out)";
      option.disabled = true;
    } else {
      allSoldOut = false;
      if (!recommendedSet) {
        option.textContent += " (Recommended)";
        option.selected = true;
        recommendedSet = true;
      }
    }
  }

  if (!recommendedSet) {
    select.value = "";
  }

  // SHOP-782: cap the PDP quantity at the selected (Recommended) week's stock.
  const chosenShipDate = select.options[select.selectedIndex]?.dataset.shipDate;
  applyQuantityCap(chosenShipDate ? qtyByDate[chosenShipDate] : undefined);

  syncPlantFields(select, variantId);
  setAddToCartDisabled(allSoldOut);

  if (allSoldOut) {
    renderNoAvailability();
  } else {
    renderDateMessage(select, variantId);
  }
}

/**
 * Renders the "Plant in {state}, {zip} from {earliest} to {latest}" message
 * into #shipping-date-label.
 *
 * @param {HTMLSelectElement} select    - The shipping week <select> element.
 * @param {string}            variantId - The active Shopify variant ID.
 * @returns {void}
 */
function renderDateMessage(select, variantId) {
  const labelElement = document.getElementById("shipping-date-label");
  if (!labelElement) {
    return;
  }

  const chosen = select.options[select.selectedIndex];
  const earliestShipDate = chosen?.dataset.shipDate ?? "";

  const today = window.bpGetDate();
  const earliestLabel =
    earliestShipDate && earliestShipDate <= today
      ? "now"
      : formatMonthDay(earliestShipDate);

  const setGid = shippingSets[variantId];
  const cutoffDates = grow.getCutoffDates();
  const range = setGid && cutoffDates ? cutoffDates[setGid] : null;
  const latestLabel = range ? formatMonthDay(addDays(range.maximum, 14)) : "";

  labelElement.innerHTML = "";
  labelElement.style.display = "";

  labelElement.appendChild(
    document.createTextNode(`Plant in ${grow.getState()}, `),
  );

  const zipSpan = document.createElement("span");
  zipSpan.textContent = grow.getZipCode();
  zipSpan.className = "bp-zip-edit";
  zipSpan.setAttribute("role", "button");
  zipSpan.setAttribute("tabindex", "0");
  zipSpan.setAttribute("aria-label", `Edit zip code: ${grow.getZipCode()}`);
  zipSpan.setAttribute("aria-haspopup", "dialog");
  zipSpan.addEventListener("click", () => grow.showCustomerZipCodeForm());
  zipSpan.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      grow.showCustomerZipCodeForm();
    }
  });
  labelElement.appendChild(zipSpan);

  if (earliestLabel && latestLabel) {
    labelElement.appendChild(
      document.createTextNode(` from ${earliestLabel} to ${latestLabel}`),
    );
  }
}

/**
 * Copies the selected option's display value into properties[Ships], and
 * populates properties[_shipping_set].
 *
 * @param {HTMLSelectElement} select    - The shipping week <select> element.
 * @param {string}            variantId - The active Shopify variant ID.
 * @returns {void}
 */
function syncPlantFields(select, variantId) {
  syncHiddenField(select);

  const setField = /** @type {HTMLInputElement | null} */ (
    document.getElementById("shipping-set-field")
  );
  if (setField) {
    setField.value = shippingSets[variantId] ?? "";
  }
}

/**
 * Copies the selected option's `data-display` value into the hidden
 * `properties[Ships]` input.
 *
 * @param {HTMLSelectElement} select - The shipping week <select> element.
 * @returns {void}
 */
function syncHiddenField(select) {
  const chosen = select.options[select.selectedIndex];
  const field = /** @type {HTMLInputElement | null} */ (
    document.getElementById(HIDDEN_DISPLAY_ID)
  );
  if (field) {
    field.value = chosen?.dataset?.display ?? "";
  }
}

/**
 * Resets the shipping week select and writes the variant's shipping message
 * (or the default fallback) into the hidden Ships field when switching to a
 * non-plant variant. Also clears the plant-specific hidden fields.
 *
 * @param {HTMLSelectElement} select    - The shipping week <select> element.
 * @param {string}            variantId - The newly selected variant ID.
 * @returns {void}
 */
function clearShippingProperties(select, variantId) {
  select.value = "";

  const field = /** @type {HTMLInputElement | null} */ (
    document.getElementById(HIDDEN_DISPLAY_ID)
  );
  if (field) {
    field.value = shippingMessages[variantId] ?? FALLBACK_SHIPPING_MESSAGE;
  }

  const setField = /** @type {HTMLInputElement | null} */ (
    document.getElementById("shipping-set-field")
  );
  if (setField) {
    setField.value = "";
  }
}

/**
 * Clears #bp-shipping-week-root for a variant that has a shipping set for a
 * different season but none for the current one.
 *
 * The "plants are out of stock for the season" message used to be rendered
 * here, but it duplicated the Notify Me message shown below the add-to-cart
 * button, so only that one is kept now.
 *
 * @returns {void}
 */
function renderPlantOutOfStock() {
  const root = document.getElementById("bp-shipping-week-root");
  if (!root) {
    return;
  }
  root.innerHTML = "";
}

/**
 * Renders a "no availability" message into #bp-shipping-week-root when the
 * customer's zone has no valid ship weeks or all options are sold out.
 *
 * @returns {void}
 */
function renderNoAvailability() {
  const root = document.getElementById("bp-shipping-week-root");
  if (!root) {
    return;
  }
  root.innerHTML = "";

  const msg = document.createElement("div");
  msg.className = "bp-no-availability";

  const line1 = document.createElement("p");
  line1.textContent = `This product is out of stock for ${grow.getState()}, `;
  const zipSpan = document.createElement("span");
  zipSpan.className = "bp-zip-edit";
  zipSpan.textContent = grow.getZipCode();
  zipSpan.setAttribute("role", "button");
  zipSpan.setAttribute("tabindex", "0");
  zipSpan.setAttribute("aria-label", `Edit zip code: ${grow.getZipCode()}`);
  zipSpan.setAttribute("aria-haspopup", "dialog");
  zipSpan.addEventListener("click", () => grow.showCustomerZipCodeForm());
  zipSpan.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      grow.showCustomerZipCodeForm();
    }
  });
  line1.appendChild(zipSpan);

  const line2 = document.createElement("p");
  line2.textContent =
    "Please click Notify Me to be emailed when this product is back in stock. In the meantime, take a look at these recommended products.";

  msg.append(line1, line2);
  root.append(msg);
}

/**
 * Renders an inline zip code form into #bp-shipping-week-root.
 * On submit, fetches grow data and persists it via setLocalStorage,
 * which triggers the onLocationChange event to re-initialize the dropdown.
 *
 * @param {HTMLSelectElement} select - The shipping week <select> element.
 * @returns {void}
 */
function renderZipPrompt(select) {
  const root = document.getElementById("bp-shipping-week-root");
  if (!root) {
    return;
  }
  root.innerHTML = "";

  const labelEl = document.createElement("label");
  labelEl.htmlFor = "bp-zip-input";
  labelEl.textContent = "Enter your Zip Code to see shipping availability";

  const form = document.createElement("div");
  form.className = "bp-zip-form";

  const input = document.createElement("input");
  input.type = "text";
  input.id = "bp-zip-input";
  input.name = "zip";
  input.placeholder = "Zip Code";
  input.maxLength = 5;
  input.setAttribute("inputmode", "numeric");
  input.setAttribute("autocomplete", "postal-code");
  input.setAttribute("aria-label", "Zip Code");
  input.className = "bp-zip-form__input";

  const errorMsg = document.createElement("p");
  errorMsg.className = "bp-zip-form__error";
  errorMsg.style.display = "none";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "bp-zip-prompt";
  button.setAttribute("aria-haspopup", "dialog");
  button.textContent = "Check Availability";

  const handleSubmit = async () => {
    const zip = input.value.trim();

    if (!/^\d{5}$/.test(zip)) {
      errorMsg.textContent = "Please enter a valid 5-digit zip code.";
      errorMsg.style.display = "";
      input.focus();
      return;
    }

    errorMsg.style.display = "none";
    button.textContent = "Checking…";
    button.disabled = true;
    input.disabled = true;

    try {
      const growData = await grow.fetchGrowData(zip);
      grow.setLocalStorage(growData);
    } catch (e) {
      errorMsg.textContent =
        "We couldn't find that zip code. Please try again.";
      errorMsg.style.display = "";
      button.textContent = "Check Availability";
      button.disabled = false;
      input.disabled = false;
      input.focus();
    }
  };

  button.addEventListener("click", handleSubmit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  });

  form.append(input, button);
  root.append(labelEl, errorMsg, form);
}

/**
 * Enables or disables the Add to Cart button and keeps its `aria-disabled`
 * attribute in sync for screen reader accessibility.
 *
 * @param {boolean} disabled - Whether the button should be disabled.
 * @returns {void}
 */
function setAddToCartDisabled(disabled) {
  const btn = /** @type {HTMLButtonElement | null} */ (
    document.querySelector(ADD_TO_CART_SELECTOR)
  );
  if (!btn) {
    return;
  }
  btn.disabled = disabled;
  btn.setAttribute("aria-disabled", String(disabled));
}

/**
 * Enables or disables the Notify Me button/link for Plant Products.
 * A Plant Product's Notify Me action is only zip-gated while it has a shipping
 * set for the current season and the customer's zip is unknown; call with
 * `false` once the zip is known, or for products with no current-season
 * shipping set at all (those must always stay available).
 *
 * Handles both markup shapes rendered by notify-me-button.liquid: the primary
 * <button> (native `disabled`) and the message-box <a> (anchors have no
 * `disabled` attribute, so `aria-disabled` plus a click-time guard in
 * notify-me-modal.liquid are used instead).
 *
 * @param {boolean} disabled - Whether the Notify Me action should be blocked.
 * @returns {void}
 */
export function setNotifyMeDisabled(disabled) {
  const btn = /** @type {HTMLButtonElement | null} */ (
    document.querySelector(".notify-me-btn")
  );
  if (btn) {
    btn.disabled = disabled;
    btn.setAttribute("aria-disabled", String(disabled));
  }

  const link = /** @type {HTMLAnchorElement | null} */ (
    document.querySelector(".notify-me-message-box [data-notify-trigger]")
  );
  if (link) {
    link.setAttribute("aria-disabled", String(disabled));
    if (disabled) {
      link.setAttribute("tabindex", "-1");
    } else {
      link.removeAttribute("tabindex");
    }
  }
}

/**
 * Shows or hides the entire Notify Me button + OOS message.
 * Used when the product/variant cannot be shipped to the customer's
 * state/zip at all — in that case neither the button nor the note should be
 * displayed, regardless of zip-gating state.
 *
 * @param {boolean} visible - Whether the Notify Me button/message should be shown.
 * @returns {void}
 */
export function setNotifyMeVisible(visible) {
  const wrapper = /** @type {HTMLElement | null} */ (
    document.querySelector("[data-notify-me-wrapper]")
  );
  if (!wrapper) {
    return;
  }
  wrapper.hidden = !visible;
}

/**
 * Shows or hides the shipping week selector UI (select + label).
 * Uses `display` style instead of the `hidden` attribute because Safari does
 * not honour `hidden` on `<select>` elements.
 *
 * @param {HTMLSelectElement} select  - The shipping week <select> element.
 * @param {boolean}           visible - Whether the UI should be visible.
 * @returns {void}
 */
function setSelectorVisible(select, visible) {
  select.style.display = visible ? "" : "none";
}

/**
 * Removes the skeleton loader placeholder once the UI is ready to render.
 * @returns {void}
 */
function removeSkeleton() {
  document.getElementById("bp-shipping-week-skeleton")?.remove();
}

/**
 * Returns "May 21" format from a YYYY-MM-DD string.
 *
 * @param {string} isoDate
 * @returns {string}
 */
export function formatMonthDay(isoDate) {
  if (!isoDate) {
    return "";
  }
  const parts = isoDate.split("-").map(Number);
  const month = parts[1];
  const day = parts[2];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[month - 1]} ${day}`;
}

/**
 * Adds n days to a YYYY-MM-DD string and returns the result as YYYY-MM-DD.
 *
 * @param {string} isoDate
 * @param {number} n
 * @returns {string}
 */
export function addDays(isoDate, n) {
  if (!isoDate) {
    return "";
  }
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + n);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Returns the last order date for a given ship date (shipDate + 6 days).
 *
 * @param {string} shipDate  YYYY-MM-DD
 * @returns {string}         YYYY-MM-DD
 */
export function getLastOrderDate(shipDate) {
  if (!shipDate) {
    return "";
  }
  return addDays(shipDate, 6);
}

/**
 * Builds an array of <option> elements for the given variant from its
 * inventory entries, sorted ascending by ship date.
 *
 * @param {string} variantId
 * @returns {HTMLOptionElement[]}
 */
function buildOptionsForVariant(variantId) {
  const variantInv = inventory?.[variantId]?.inventory ?? [];
  return variantInv
    .map((entry) => {
      const shipDate = entry.shipDate ?? "";
      const lastOrderDate = getLastOrderDate(shipDate);
      const display = `${formatMonthDay(shipDate)} - ${formatMonthDay(addDays(shipDate, 11))}`;
      const opt = document.createElement("option");
      opt.value = shipDate;
      opt.dataset.shipDate = shipDate;
      opt.dataset.lastOrderDate = lastOrderDate;
      opt.dataset.display = display;
      opt.textContent = `Ships ${display}`;
      return opt;
    })
    .sort((a, b) =>
      (a.dataset.shipDate ?? "").localeCompare(b.dataset.shipDate ?? ""),
    );
}
